import { type TFunction } from "i18next"

import { type AppointmentType, type Event } from "../../types/types.ts"
import { appointmentTypeLabelKey } from "../constants/appointment-types.ts"

type CalendarEventRecord = Pick<
  Event,
  "lead" | "project" | "contact" | "category"
> & {
  // The meeting's own address (arriving with the meeting-location work);
  // until every stored event carries it, the lead's contact address stands in.
  address?: string | null
}

interface CalendarEventCardInput {
  title?: string | null
  record?: CalendarEventRecord
}

// Only where the shared label reads badly as a headline; every other category
// resolves through appointmentTypeLabelKey so the card and the Appointment
// Type row cannot show two spellings of one type.
const HEADLINE_OVERRIDE_KEYS: Partial<Record<AppointmentType, string>> = {
  LEAD_MEETING: "calendarEvent.headline.meeting"
}

const calendarEventCardTitle = (
  { title, record }: CalendarEventCardInput,
  t: TFunction
): string => {
  const category = record?.category
  if (!category) return title || "-"

  return t(
    HEADLINE_OVERRIDE_KEYS[category] ?? appointmentTypeLabelKey(category)
  )
}

const recordParts = (
  record: CalendarEventRecord | undefined,
  t: TFunction
): string[] => {
  const source = record?.project || record?.lead
  if (!source) return []

  const kind = record?.project ? "project" : "lead"
  const label = source.uid
    ? t(`calendarEvent.record.${kind}WithId`, { uid: source.uid })
    : t(`calendarEvent.record.${kind}`)
  const customer = record?.contact?.customer_name?.trim()
  return customer ? [label, customer] : [label]
}

const calendarEventRecordSubtitle = (
  { record }: CalendarEventCardInput,
  t: TFunction
): string | null => {
  return recordParts(record, t).join(" · ") || null
}

// The grid card's single headline row: type, record id and customer in one
// line, so a 30-minute slot holds the whole identity ("Meeting – Lead ID
// 94JYL48A7W – Rubee Singh").
const calendarEventCardLine = (
  input: CalendarEventCardInput,
  t: TFunction
): string => {
  return [calendarEventCardTitle(input, t), ...recordParts(input.record, t)]
    .filter(Boolean)
    .join(" – ")
}

// The card's second row. A follow-up is a call, not a visit — it stays a
// one-line card with no address by design.
const calendarEventCardAddress = ({
  record
}: CalendarEventCardInput): string | null => {
  if (record?.category === "FOLLOW_UP") return null

  const own = record?.address?.trim()
  if (own) return own

  const contact = record?.contact
  const fallback = [contact?.address?.trim(), contact?.city?.trim()]
    .filter(Boolean)
    .join(", ")
  return fallback || null
}

export {
  calendarEventCardAddress,
  calendarEventCardLine,
  calendarEventCardTitle,
  calendarEventRecordSubtitle
}
